/**
 * @module components/quiz
 */

import {AttributeChangeObservable} from '@lernetz/ts-lib/src/core/model/Model';
import {Resolvable} from '@lernetz/ts-lib/src/components/Resolve';


/**
 * The input state of a quiz.
 *
 *  * `'none'`: The quiz has not received any user input,
 *    or it has been reset to its initial state.
 *
 *  * `'incomplete'`: The quiz has received some user input,
 *    but it is not yet possible to determine
 *    whether the given input is correct or incorrect.
 *
 *  * `'incorrect'`: The quiz has received sufficient input
 *    to determine that it is incorrect.
 *
 *  * `'correct'`: The quiz has received sufficient input
 *    to determine that it is correct.
 */
export type QuizInputState = 'none' | 'incomplete' | 'incorrect' | 'correct';


/**
 * A quiz.
 */
export interface Quiz extends AttributeChangeObservable {

    /**
     * Gets the current input state of this quiz (either `'none'`, `'incomplete'`, `'incorrect'`, or `'correct'`).
     *
     * Implementations should raise [[change]] events for this property.
     */
    inputState: QuizInputState;

    /**
     * This method together with the [[QuizVisitor]] type implements the *Visitor Pattern*.
     * Derived classes should dispatch to the appropriate `visit…Quiz` method on [[visitor]].
     * @param visitor The [[QuizVisitor]] to which this method will dispatch.
     */
    accept<TResult = void>(visitor: QuizVisitor<TResult>): TResult;
}


/**
 * This abstract base class together with [[Quiz.accept]] implements the *Visitor Pattern*.
 * Each [[Quiz]] type's `accept` method dispatches (delegates) to one of the `visit…Quiz`
 * methods of some visitor.
 *
 * [[QuizVisitor]] implementations do not need to support all quiz types. Each implementing
 * class can choose which quiz types by implementing selected quiz types' dedicated
 * `…QuizVisitor` interfaces. For example, for [[ChoiceQuiz]], there exists a
 * [[ChoiceQuizVisitor]] interface in the `components/choice-quiz` module. A visitor
 * that supports `ChoiceQuiz` should implement `ChoiceQuizVisitor`.
 */
export abstract class QuizVisitor<TResult = void> {

    public visit(quiz: Quiz): TResult {
        return quiz.accept<TResult>(this);
    }
}


export {CanShowCorrectness, canShowCorrectness} from './CanShowCorrectness';
